import clsx from 'clsx';
import React, { AriaAttributes } from 'react';

export type HeaderTextProps = {
  children: React.ReactNode;
  /**
   * Renders HeaderText with the heading level provided.
   *
   * The heading level defaults to the following sizes:
   * - `1`: `lg`
   * - `2`: `md`
   * - `3`: `sm`
   * - `4`: `sm`
   * - `5`: `sm`
   * - `6`: `sm`
   *
   * Provide a `size` for alternative behavior.
   */
  level: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Transforms HeaderText to be different predefined sizes, listed below.
   * - `xl`: 48px
   * - `lg`: 34px on mobile, 40px on desktop
   * - `md`: 24px on mobile, 28px on desktop
   * - `sm`: 20px
   *
   * @defaultValue `lg` if `level` is 1, `md` if `level` is 2, `sm` if `level` is 3-6
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
} & Pick<AriaAttributes, 'aria-label'>;

const levelToSizeDefaultMapping: Record<HeaderTextProps['level'], NonNullable<HeaderTextProps['size']>> = {
  1: 'lg',
  2: 'md',
  3: 'sm',
  4: 'sm',
  5: 'sm',
  6: 'sm',
};

export const HeaderText = ({ 'aria-label': ariaLabel, children, level, size }: HeaderTextProps): JSX.Element => {
  const htmlElement = `h${level}`;
  const finalSize = size ?? levelToSizeDefaultMapping[level];
  return React.createElement(
    htmlElement,
    {
      className: clsx('capitalize font-bold', {
        'text-20 tracking-widest': finalSize === 'sm',
        'text-24 md:text-28 tracking-wider': finalSize === 'md',
        'text-34 md:text-40 tracking-wide': finalSize === 'lg',
        'text-48 tracking-[0.005em]': finalSize === 'xl',
      }),
      'aria-label': ariaLabel,
    },
    children,
  );
};
